import type {
  AgentFocusTarget,
  AgentSessionMessage,
} from "@/lib/agentRunTransport";

const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export function parseStoredFocusTarget(
  value: unknown,
): AgentFocusTarget | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const originalFileId = record.originalFileId;
  const focusFileId = record.focusFileId;
  const selectionMethod = record.selectionMethod;
  const score = record.score;
  const box = record.boxXYXY;
  const imageWidth = record.imageWidth;
  const imageHeight = record.imageHeight;

  if (
    (typeof originalFileId !== "string" && typeof originalFileId !== "number") ||
    (typeof focusFileId !== "string" && typeof focusFileId !== "number") ||
    !["point", "lasso", "text"].includes(String(selectionMethod)) ||
    !finite(score) ||
    !Array.isArray(box) ||
    box.length !== 4 ||
    !box.every(finite) ||
    !Number.isSafeInteger(imageWidth) ||
    !Number.isSafeInteger(imageHeight)
  ) {
    return null;
  }

  const label =
    typeof record.label === "string" && record.label.trim()
      ? record.label.trim()
      : undefined;
  const textPrompt =
    typeof record.textPrompt === "string" && record.textPrompt.trim()
      ? record.textPrompt.trim()
      : undefined;
  if (selectionMethod === "text" && !textPrompt) return null;
  if (score < 0 || score > 1 || Number(imageWidth) < 1 || Number(imageHeight) < 1) {
    return null;
  }
  const [x1, y1, x2, y2] = box as [number, number, number, number];
  if (
    x1 < 0 ||
    y1 < 0 ||
    x2 <= x1 ||
    y2 <= y1 ||
    x2 > Number(imageWidth) ||
    y2 > Number(imageHeight)
  ) {
    return null;
  }

  return {
    originalFileId: String(originalFileId),
    focusFileId: String(focusFileId),
    ...(label ? { label } : {}),
    selectionMethod: selectionMethod as AgentFocusTarget["selectionMethod"],
    ...(textPrompt ? { textPrompt } : {}),
    score,
    boxXYXY: box as AgentFocusTarget["boxXYXY"],
    imageWidth: Number(imageWidth),
    imageHeight: Number(imageHeight),
  };
}

export function focusTargetFromMessages(
  messages: AgentSessionMessage[],
): AgentFocusTarget | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "user") continue;
    const parsed = parseStoredFocusTarget(message.metadata?.focusTarget);
    if (parsed) return parsed;
  }
  return null;
}

export function latestGeneratingObjectRiskRun(
  messages: AgentSessionMessage[],
): { runId: string; after: number } | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "assistant") continue;
    const metadata = message.metadata;
    if (
      metadata?.is_generating !== true ||
      metadata?.run_agent_id !== "object-risk-assessment" ||
      (typeof metadata.run_id !== "string" &&
        typeof metadata.run_id !== "number")
    ) {
      continue;
    }
    const after = Number(metadata.last_event_sequence ?? 0);
    return {
      runId: String(metadata.run_id),
      after: Number.isSafeInteger(after) && after >= 0 ? after : 0,
    };
  }
  return null;
}
